import { useNavigate } from '@tanstack/react-router';
import moment from 'moment';
import { AnimatePresence } from 'motion/react';
import { useState } from 'react';

import { useAppStore } from '@common/stores/useAppStore';
import { Indicator } from '@common/ui/Indicator';
import { useEventEmit } from '@common/utils/events';
import { getApiErrorInfo, isRateLimitedError } from '@common/utils/fetcher';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { MatchDialog } from '@entities/dating/ui/MatchDialog';
import { SuperlikeLikeCard } from '@entities/likes/ui/SuperlikeLikeCard';
import { useSuperlikeSessionStore } from '@entities/superlike/model';

import { usePostSwipe } from '@services/hooks/MatchesService';
import { type IncomingSuperlikeCard } from '@services/SuperlikesService';
import { type User } from '@services/UsersService';

import styles from './SuperlikesSection.module.scss';

export type SuperlikesSectionProps = {
  cards: IncomingSuperlikeCard[];
};

const RATE_LIMITED_NOTICE = {
  title: 'Слишком много действий',
  description: 'Попробуйте ещё раз чуть позже',
};

/**
 * «Суперлайки» above the likes (board 2.3): free for everyone, unlike the
 * likes grid. A like back is a match (the sender already liked), so it opens
 * the match dialog; an answer is remembered for the session, here and in
 * the feed.
 */
export const SuperlikesSection = ({ cards }: SuperlikesSectionProps) => {
  const navigate = useNavigate();
  const emit = useEventEmit();
  const user = useAuthStore((state) => state.user);
  const swipeResetTime = useAuthStore(
    (state) => state.swipe_status?.reset_time,
  );
  const decrementLikesCount = useAppStore((state) => state.decrementLikesCount);
  const rememberAnsweredSender = useSuperlikeSessionStore(
    (state) => state.rememberAnsweredSender,
  );
  const { trigger: triggerPostSwipe } = usePostSwipe();

  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [matchProfile, setMatchProfile] = useState<User>();

  const setPending = (profileId: string, isPending: boolean) =>
    setPendingIds((prev) => {
      const next = new Set(prev);

      if (isPending) next.add(profileId);
      else next.delete(profileId);

      return next;
    });

  const answer = async (
    action: 'like' | 'pass',
    profile: IncomingSuperlikeCard,
  ) => {
    if (user?.approval_status?.status !== 'approved') {
      emit('warning', {
        title: 'Ваш профиль не подтвержден',
        description:
          'Вы сможете лайкать пользователей в ответ после подтверждения анкеты модератором',
      });

      return;
    }

    if (pendingIds.has(profile.id)) return;

    setPending(profile.id, true);

    try {
      await triggerPostSwipe({
        target_user_id: profile.id,
        action,
        isLikeBack: action === 'like',
      });

      rememberAnsweredSender(profile.id);
      decrementLikesCount();

      if (action === 'like') {
        // The dialog sits at the top of the page, and the page scrolls in
        // #root: a card further down would open it off-screen.
        document.querySelector('#root')?.scrollTo({ top: 0 });
        setMatchProfile({ ...profile, liked_me: true });
      }
    } catch (error) {
      if (isRateLimitedError(error)) {
        emit('info', RATE_LIMITED_NOTICE);
      } else if (getApiErrorInfo(error).status === 429) {
        emit('error', {
          title: 'Лимит свайпов исчерпан',
          description: `До обновления свайпов осталось ${moment(swipeResetTime).fromNow(true)}`,
        });
      } else {
        emit('error', {
          title: 'Ошибка отправки запроса',
          description: 'Попробуйте позже',
        });
      }
    } finally {
      setPending(profile.id, false);
    }
  };

  return (
    <>
      <section
        className={styles.superlikesSection}
        aria-label="Суперлайки"
        data-test-id="likes-superlikes-section"
      >
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h2 className={styles.title}>Суперлайки</h2>
            <Indicator value={cards.length} size={600} />
          </div>
          <p className={styles.subtitle}>Их видно без Premium</p>
        </div>

        <div className={styles.grid}>
          {cards.map((profile) => (
            <SuperlikeLikeCard
              key={profile.id}
              profile={profile}
              isPending={pendingIds.has(profile.id)}
              onOpen={() =>
                navigate({
                  to: `/dating/profile/${profile.id}`,
                  search: { canSwipes: true },
                })
              }
              onLike={() => void answer('like', profile)}
              onPass={() => void answer('pass', profile)}
            />
          ))}
        </div>
      </section>

      <AnimatePresence>
        {matchProfile && (
          <MatchDialog
            matchProfile={matchProfile}
            onClose={() => setMatchProfile(undefined)}
          />
        )}
      </AnimatePresence>
    </>
  );
};
